import { Card, CardBody, CardHeader, Chip, ChipProps } from "@heroui/react";
import { Icon } from "@iconify/react";
import {
	paidTotal,
	paymentStatusLabels as statusLabels,
	requestCost,
	serviceLevelLabels,
	serviceLevelOf,
	TPaymentStatus,
	TRequest,
} from "@shared/entities";

const statusColors: Record<TPaymentStatus, ChipProps["color"]> = {
	pending: "warning",
	paid: "success",
	refund: "default",
	partially_paid: "primary",
	cancelled: "danger",
};

const shekel = (amount: number) => `${amount.toLocaleString("he-IL")} ₪`;

/**
 * Admin-only summary: service level, price, paid, balance, payments. Display
 * only — level and price are edited in the request form ("ערוך") like every
 * other field, and the server keeps the payment status in step with them.
 */
export function ServiceAndPaymentCard({ request }: { request: TRequest }) {
	const cost = requestCost(request);
	const total = paidTotal(request);
	const payments = request.paymentDetails?.payments ?? [];
	const status = request.paymentDetails?.status ?? "pending";

	return (
		<Card>
			<CardHeader className="flex items-center justify-between">
				<h3 className="text-lg font-semibold">רמת שירות ותשלום</h3>
				<Chip color={statusColors[status]} variant="flat" size="sm">
					{statusLabels[status]}
				</Chip>
			</CardHeader>
			<CardBody className="text-start space-y-4">
				<div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
					<div>
						<p className="text-small text-default-500 mb-1">רמת שירות</p>
						<p className="font-medium">{serviceLevelLabels[serviceLevelOf(request)]}</p>
					</div>
					<div>
						<p className="text-small text-default-500 mb-1">מחיר</p>
						<p className="font-medium">{cost ? shekel(cost) : "לא נקבע — כל תשלום נחשב ״שולם״"}</p>
					</div>
					<div>
						<p className="text-small text-default-500 mb-1">שולם</p>
						<p className="font-medium">{shekel(total)}</p>
					</div>
					<div>
						<p className="text-small text-default-500 mb-1">יתרה</p>
						<p className={`font-medium ${cost && cost > total ? "text-danger" : "text-success"}`}>
							{cost ? shekel(Math.max(cost - total, 0)) : "—"}
						</p>
					</div>
				</div>

				{payments.length > 0 && (
					<div className="pt-4 border-t border-default-200 space-y-2">
						<p className="text-small text-default-500">תשלומים</p>
						{payments.map((payment) => (
							<div
								key={payment.transactionId ?? `${payment.date}-${payment.amount}`}
								className="flex items-center justify-between p-3 rounded-lg bg-content2"
							>
								<span className="text-sm flex items-center gap-2">
									<Icon icon="lucide:calendar" className="text-default-400" />
									{new Date(payment.date).toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" })}
									{payment.transactionId && <span className="text-default-400">עסקה {payment.transactionId}</span>}
								</span>
								<span className="font-medium">{shekel(payment.amount)}</span>
							</div>
						))}
					</div>
				)}
			</CardBody>
		</Card>
	);
}
